/**
 * Rapor Yönetimi Servis Katmanı
 *
 * Personel paneli için rapor CRUD işlemlerini ve
 * rapor verisi oluşturma işlemlerini yönetir.
 * Supabase client üzerinden RLS korumalı sorgular çalıştırır.
 *
 * @module reportService
 */

import { supabase } from '../lib/supabase';

const reportService = {
  // ===========================================================
  // Rapor Listesi
  // ===========================================================

  /**
   * Raporları getirir. Filtreleme ve sayfalama desteği vardır.
   * @param {Object} filters - Filtreleme seçenekleri
   * @param {string} [filters.report_type] - daily | weekly | monthly | custom | customer_analysis | property_performance
   * @param {string} [filters.status] - draft | submitted | approved
   * @param {string} [filters.staff_id] - Staff UUID
   * @param {Date} [filters.startDate] - Başlangıç tarihi
   * @param {Date} [filters.endDate] - Bitiş tarihi
   * @param {number} [filters.limit=20] - Sayfa başına kayıt sayısı
   * @param {number} [filters.offset=0] - Kaydırma miktarı
   * @param {string} [filters.sortBy='created_at'] - Sıralama alanı
   * @param {boolean} [filters.ascending=false] - Artan sıralama
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getReports(filters = {}) {
    try {
      const {
        report_type,
        status,
        staff_id,
        startDate,
        endDate,
        limit = 20,
        offset = 0,
        sortBy = 'created_at',
        ascending = false,
      } = filters;

      let query = supabase
        .from('staff_reports')
        .select('*', { count: 'exact' });

      // Filtreler
      if (report_type) {
        query = query.eq('report_type', report_type);
      }
      if (status) {
        query = query.eq('status', status);
      }
      if (staff_id) {
        query = query.eq('staff_id', staff_id);
      }
      if (startDate) {
        query = query.gte('date_range_start', startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        query = query.lte('date_range_end', endDate.toISOString().split('T')[0]);
      }

      // Sıralama
      query = query.order(sortBy, { ascending });

      // Sayfalama
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      return { data, count, error };
    } catch (error) {
      console.error('getReports error:', error);
      return { data: null, count: 0, error };
    }
  },

  // ===========================================================
  // Tek Rapor
  // ===========================================================

  /**
   * Tek bir raporu ID ile getirir.
   * @param {string} reportId - Rapor UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async getReportById(reportId) {
    try {
      const { data, error } = await supabase
        .from('staff_reports')
        .select('*')
        .eq('id', reportId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('getReportById error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor Oluştur
  // ===========================================================

  /**
   * Yeni bir rapor oluşturur.
   * @param {Object} reportData - Rapor verileri
   * @param {string} reportData.title - Rapor başlığı
   * @param {string} reportData.report_type - daily | weekly | monthly | custom | customer_analysis | property_performance
   * @param {Object} [reportData.content] - Rapor içeriği (JSONB)
   * @param {string} [reportData.summary] - Rapor özeti
   * @param {Date} [reportData.date_range_start] - Başlangıç tarihi
   * @param {Date} [reportData.date_range_end] - Bitiş tarihi
   * @param {string} [reportData.status] - draft | submitted | approved
   * @returns {{ data: object|null, error: object|null }}
   */
  async createReport(reportData) {
    try {
      const insertData = {
        title: reportData.title,
        report_type: reportData.report_type,
        content: reportData.content || {},
        summary: reportData.summary || null,
        status: reportData.status || 'draft',
      };

      if (reportData.date_range_start) {
        insertData.date_range_start = reportData.date_range_start instanceof Date
          ? reportData.date_range_start.toISOString().split('T')[0]
          : reportData.date_range_start;
      }
      if (reportData.date_range_end) {
        insertData.date_range_end = reportData.date_range_end instanceof Date
          ? reportData.date_range_end.toISOString().split('T')[0]
          : reportData.date_range_end;
      }

      const { data, error } = await supabase
        .from('staff_reports')
        .insert(insertData)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('createReport error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor Güncelle
  // ===========================================================

  /**
   * Mevcut bir raporu günceller.
   * @param {string} reportId - Rapor UUID'si
   * @param {Object} reportData - Güncellenecek veriler
   * @returns {{ data: object|null, error: object|null }}
   */
  async updateReport(reportId, reportData) {
    try {
      const allowedFields = [
        'title', 'report_type', 'content', 'summary',
        'date_range_start', 'date_range_end', 'status',
      ];

      const updateData = {};
      allowedFields.forEach(field => {
        if (reportData[field] !== undefined) {
          if (field === 'date_range_start' || field === 'date_range_end') {
            updateData[field] = reportData[field] instanceof Date
              ? reportData[field].toISOString().split('T')[0]
              : reportData[field];
          } else {
            updateData[field] = reportData[field];
          }
        }
      });

      const { data, error } = await supabase
        .from('staff_reports')
        .update(updateData)
        .eq('id', reportId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('updateReport error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor Sil
  // ===========================================================

  /**
   * Bir raporu siler.
   * @param {string} reportId - Rapor UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async deleteReport(reportId) {
    try {
      const { data, error } = await supabase
        .from('staff_reports')
        .delete()
        .eq('id', reportId);

      return { data, error };
    } catch (error) {
      console.error('deleteReport error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor Verisi Oluşturma - Günlük Rapor
  // ===========================================================

  /**
   * Belirli bir tarihteki günlük rapor verisini oluşturur.
   * @param {Date} date - Rapor tarihi
   * @returns {{ data: object|null, error: object|null }}
   */
  async generateDailyReportData(date) {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const startISO = startOfDay.toISOString();
      const endISO = endOfDay.toISOString();

      // Paralel sorgular
      const [
        newCustomersResult,
        totalActivitiesResult,
        activitiesByTypeResult,
        newPropertiesResult,
        followUpsCompletedResult,
      ] = await Promise.all([
        // Bugün eklenen müşteriler
        supabase
          .from('customers')
          .select('id, first_name, last_name, status, lead_source', { count: 'exact' })
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // Bugün yapılan toplam aktivite sayısı
        supabase
          .from('customer_activities')
          .select('id', { count: 'exact', head: true })
          .gte('activity_date', startISO)
          .lte('activity_date', endISO),

        // Aktivite tiplerini say
        supabase
          .from('customer_activities')
          .select('activity_type')
          .gte('activity_date', startISO)
          .lte('activity_date', endISO),

        // Bugün eklenen ilanlar
        supabase
          .from('properties')
          .select('id, title_tr, title_en, type, status', { count: 'exact' })
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // Bugün yapılması gereken ve tamamlanan follow-up'lar
        supabase
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .gte('next_follow_up', startISO.split('T')[0])
          .lt('next_follow_up', endISO.split('T')[0]),
      ]);

      // Aktivite tiplerini grupla
      const activityStats = {
        call: 0,
        email: 0,
        meeting: 0,
        note: 0,
        property_showing: 0,
        follow_up: 0,
      };

      activitiesByTypeResult.data?.forEach(activity => {
        if (activity.activity_type && activityStats.hasOwnProperty(activity.activity_type)) {
          activityStats[activity.activity_type]++;
        }
      });

      // Rapor verisini oluştur
      const reportData = {
        date: startISO.split('T')[0],
        customers: {
          total_new: newCustomersResult.count || 0,
          details: newCustomersResult.data || [],
        },
        activities: {
          total: totalActivitiesResult.count || 0,
          by_type: activityStats,
        },
        properties: {
          total_new: newPropertiesResult.count || 0,
          details: newPropertiesResult.data || [],
        },
        follow_ups: {
          scheduled: followUpsCompletedResult.count || 0,
        },
      };

      return { data: reportData, error: null };
    } catch (error) {
      console.error('generateDailyReportData error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor Verisi Oluşturma - Müşteri Analizi
  // ===========================================================

  /**
   * Belirli tarih aralığındaki müşteri analiz raporu verisini oluşturur.
   * @param {Date} startDate - Başlangıç tarihi
   * @param {Date} endDate - Bitiş tarihi
   * @returns {{ data: object|null, error: object|null }}
   */
  async generateCustomerAnalysisData(startDate, endDate) {
    try {
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();

      const [
        totalCustomersResult,
        customersByStatusResult,
        customersBySourceResult,
        activitiesResult,
        conversionResult,
      ] = await Promise.all([
        // Dönem içi toplam yeni müşteri
        supabase
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // Durumlara göre müşteri dağılımı
        supabase
          .from('customers')
          .select('status')
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // Lead kaynaklarına göre dağılım
        supabase
          .from('customers')
          .select('lead_source')
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // Dönem içi aktivite sayısı
        supabase
          .from('customer_activities')
          .select('id', { count: 'exact', head: true })
          .gte('activity_date', startISO)
          .lte('activity_date', endISO),

        // Conversion oranı için closed müşteriler
        supabase
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', startISO)
          .lte('created_at', endISO)
          .eq('status', 'closed'),
      ]);

      // Durumlara göre grupla
      const statusStats = {
        new: 0,
        contacted: 0,
        interested: 0,
        not_interested: 0,
        closed: 0,
      };

      customersByStatusResult.data?.forEach(customer => {
        if (customer.status && statusStats.hasOwnProperty(customer.status)) {
          statusStats[customer.status]++;
        }
      });

      // Lead kaynaklarına göre grupla
      const sourceStats = {};
      customersBySourceResult.data?.forEach(customer => {
        if (customer.lead_source) {
          sourceStats[customer.lead_source] = (sourceStats[customer.lead_source] || 0) + 1;
        }
      });

      // Conversion oranını hesapla
      const totalNewCustomers = totalCustomersResult.count || 0;
      const closedCustomers = conversionResult.count || 0;
      const conversionRate = totalNewCustomers > 0 
        ? ((closedCustomers / totalNewCustomers) * 100).toFixed(2)
        : 0;

      const reportData = {
        date_range: {
          start: startISO.split('T')[0],
          end: endISO.split('T')[0],
        },
        summary: {
          total_new_customers: totalNewCustomers,
          total_activities: activitiesResult.count || 0,
          closed_customers: closedCustomers,
          conversion_rate: parseFloat(conversionRate),
        },
        by_status: statusStats,
        by_source: sourceStats,
      };

      return { data: reportData, error: null };
    } catch (error) {
      console.error('generateCustomerAnalysisData error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor Verisi Oluşturma - İlan Performansı
  // ===========================================================

  /**
   * Belirli tarih aralığındaki ilan performans raporu verisini oluşturur.
   * @param {Date} startDate - Başlangıç tarihi
   * @param {Date} endDate - Bitiş tarihi
   * @returns {{ data: object|null, error: object|null }}
   */
  async generatePropertyPerformanceData(startDate, endDate) {
    try {
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();

      const [
        newPropertiesResult,
        propertiesByTypeResult,
        propertiesByStatusResult,
        totalPropertiesResult,
        activePropertiesResult,
      ] = await Promise.all([
        // Dönem içi yeni ilanlar
        supabase
          .from('properties')
          .select('id, title_tr, title_en, type, status, price_gbp, views_count, contact_count', { count: 'exact' })
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // İlan tiplerine göre dağılım
        supabase
          .from('properties')
          .select('type')
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // İlan durumlarına göre dağılım (satılık/kiralık)
        supabase
          .from('properties')
          .select('status')
          .gte('created_at', startISO)
          .lte('created_at', endISO),

        // Toplam ilan sayısı
        supabase
          .from('properties')
          .select('id', { count: 'exact', head: true }),

        // Aktif ilan sayısı
        supabase
          .from('properties')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true),
      ]);

      // Tiplere göre grupla
      const typeStats = {
        villa: 0,
        apartman: 0,
        arsa: 0,
        ticari: 0,
      };

      propertiesByTypeResult.data?.forEach(property => {
        if (property.type && typeStats.hasOwnProperty(property.type)) {
          typeStats[property.type]++;
        }
      });

      // Durumlara göre grupla
      const statusStats = {
        satilik: 0,
        kiralik: 0,
      };

      propertiesByStatusResult.data?.forEach(property => {
        if (property.status && statusStats.hasOwnProperty(property.status)) {
          statusStats[property.status]++;
        }
      });

      // En çok görüntülenen ilanlar
      const topViewedProperties = [...(newPropertiesResult.data || [])]
        .sort((a, b) => (b.views_count || 0) - (a.views_count || 0))
        .slice(0, 10);

      // En çok iletişim alınan ilanlar
      const topContactedProperties = [...(newPropertiesResult.data || [])]
        .sort((a, b) => (b.contact_count || 0) - (a.contact_count || 0))
        .slice(0, 10);

      const reportData = {
        date_range: {
          start: startISO.split('T')[0],
          end: endISO.split('T')[0],
        },
        summary: {
          total_properties: totalPropertiesResult.count || 0,
          active_properties: activePropertiesResult.count || 0,
          new_properties_in_period: newPropertiesResult.count || 0,
        },
        by_type: typeStats,
        by_status: statusStats,
        top_viewed: topViewedProperties.map(p => ({
          id: p.id,
          title: p.title_tr || p.title_en,
          type: p.type,
          views: p.views_count || 0,
        })),
        top_contacted: topContactedProperties.map(p => ({
          id: p.id,
          title: p.title_tr || p.title_en,
          type: p.type,
          contacts: p.contact_count || 0,
        })),
      };

      return { data: reportData, error: null };
    } catch (error) {
      console.error('generatePropertyPerformanceData error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Hızlı Rapor Oluşturma
  // ===========================================================

  /**
   * Günlük rapor oluşturur ve kaydeder.
   * @param {Date} date - Rapor tarihi
   * @param {string} title - Rapor başlığı
   * @returns {{ data: object|null, error: object|null }}
   */
  async createDailyReport(date, title) {
    try {
      // Rapor verisini oluştur
      const { data: reportData, error: dataError } = await this.generateDailyReportData(date);
      
      if (dataError) {
        return { data: null, error: dataError };
      }

      // Özet oluştur
      const summary = `Günlük rapor: ${reportData.customers.total_new} yeni müşteri, ${reportData.activities.total} aktivite, ${reportData.properties.total_new} yeni ilan.`;

      // Raporu kaydet
      return await this.createReport({
        title: title || `Günlük Rapor - ${date.toISOString().split('T')[0]}`,
        report_type: 'daily',
        content: reportData,
        summary,
        date_range_start: date,
        date_range_end: date,
        status: 'draft',
      });
    } catch (error) {
      console.error('createDailyReport error:', error);
      return { data: null, error };
    }
  },

  /**
   * Müşteri analiz raporu oluşturur ve kaydeder.
   * @param {Date} startDate - Başlangıç tarihi
   * @param {Date} endDate - Bitiş tarihi
   * @param {string} title - Rapor başlığı
   * @returns {{ data: object|null, error: object|null }}
   */
  async createCustomerAnalysisReport(startDate, endDate, title) {
    try {
      // Rapor verisini oluştur
      const { data: reportData, error: dataError } = await this.generateCustomerAnalysisData(startDate, endDate);
      
      if (dataError) {
        return { data: null, error: dataError };
      }

      // Özet oluştur
      const summary = `Müşteri analizi: ${reportData.summary.total_new_customers} yeni müşteri, %${reportData.summary.conversion_rate} conversion oranı.`;

      // Raporu kaydet
      return await this.createReport({
        title: title || `Müşteri Analizi - ${startDate.toISOString().split('T')[0]} / ${endDate.toISOString().split('T')[0]}`,
        report_type: 'customer_analysis',
        content: reportData,
        summary,
        date_range_start: startDate,
        date_range_end: endDate,
        status: 'draft',
      });
    } catch (error) {
      console.error('createCustomerAnalysisReport error:', error);
      return { data: null, error };
    }
  },

  /**
   * İlan performans raporu oluşturur ve kaydeder.
   * @param {Date} startDate - Başlangıç tarihi
   * @param {Date} endDate - Bitiş tarihi
   * @param {string} title - Rapor başlığı
   * @returns {{ data: object|null, error: object|null }}
   */
  async createPropertyPerformanceReport(startDate, endDate, title) {
    try {
      // Rapor verisini oluştur
      const { data: reportData, error: dataError } = await this.generatePropertyPerformanceData(startDate, endDate);
      
      if (dataError) {
        return { data: null, error: dataError };
      }

      // Özet oluştur
      const summary = `İlan performansı: ${reportData.summary.new_properties_in_period} yeni ilan, ${reportData.summary.active_properties} aktif ilan.`;

      // Raporu kaydet
      return await this.createReport({
        title: title || `İlan Performans Raporu - ${startDate.toISOString().split('T')[0]} / ${endDate.toISOString().split('T')[0]}`,
        report_type: 'property_performance',
        content: reportData,
        summary,
        date_range_start: startDate,
        date_range_end: endDate,
        status: 'draft',
      });
    } catch (error) {
      console.error('createPropertyPerformanceReport error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Rapor İstatistikleri
  // ===========================================================

  /**
   * Rapor istatistiklerini getirir.
   * @returns {{ data: object|null, error: object|null }}
   */
  async getReportStats() {
    try {
      const [totalResult, draftResult, submittedResult, approvedResult] = await Promise.all([
        supabase.from('staff_reports').select('id', { count: 'exact', head: true }),
        supabase.from('staff_reports').select('id', { count: 'exact', head: true }).eq('status', 'draft'),
        supabase.from('staff_reports').select('id', { count: 'exact', head: true }).eq('status', 'submitted'),
        supabase.from('staff_reports').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
      ]);

      return {
        data: {
          total: totalResult.count || 0,
          draft: draftResult.count || 0,
          submitted: submittedResult.count || 0,
          approved: approvedResult.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('getReportStats error:', error);
      return { data: null, error };
    }
  },
};

export default reportService;
